import { motion } from 'framer-motion';
import data from '../data/data.json';

const TYPE_CONFIG = {
  internship: { label: 'Industry Internship', color: '#E8956D', bg: 'rgba(232, 149, 109, 0.14)', border: 'rgba(232, 149, 109, 0.35)' },
  research: { label: 'Research', color: '#2D1753', bg: 'rgba(45, 23, 83, 0.1)', border: 'rgba(45, 23, 83, 0.25)' },
  leadership: { label: 'Leadership', color: '#3a7050', bg: 'rgba(58, 112, 80, 0.14)', border: 'rgba(58, 112, 80, 0.3)' },
  freelance: { label: 'Freelance & Projects', color: '#5040a0', bg: 'rgba(80, 64, 160, 0.14)', border: 'rgba(80, 64, 160, 0.3)' },
};

export default function Experience() {
  const { experience } = data;

  return (
    <section id="experience" className="section">
      <div className="container">
        <motion.p
          className="section-label"
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          Track Record
        </motion.p>
        <motion.h2
          className="section-title foil-shine"
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.1 }}
        >
          Experience & Growth
        </motion.h2>

        <div style={{ position: 'relative', maxWidth: '820px', margin: '0 auto' }}>
          {/* Luminous Animated Timeline Conduit Line */}
          <motion.div
            initial={{ scaleY: 0 }}
            whileInView={{ scaleY: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1] }}
            style={{
              position: 'absolute',
              left: '19px',
              top: '16px',
              bottom: '16px',
              width: '2px',
              background: 'linear-gradient(to bottom, var(--accent) 0%, rgba(45, 23, 83, 0.18) 100%)',
              boxShadow: '0 0 8px rgba(232, 149, 109, 0.4)',
              transformOrigin: 'top',
              zIndex: 0,
            }}
          />

          <div style={{ display: 'flex', flexDirection: 'column', gap: '3rem' }}>
            {experience.map((item, i) => {
              const conf = TYPE_CONFIG[item.type] || TYPE_CONFIG.internship;
              return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, x: -24 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, margin: '-40px' }}
                  transition={{ duration: 0.6, delay: i * 0.12, ease: [0.16, 1, 0.3, 1] }}
                  style={{ display: 'flex', gap: '2rem', alignItems: 'flex-start', position: 'relative', zIndex: 1 }}
                >
                  {/* Glowing Milestone Node */}
                  <div
                    style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '50%',
                      background: 'var(--bg)',
                      border: `2px solid ${conf.color}`,
                      boxShadow: `0 0 16px ${conf.bg}`,
                      flexShrink: 0,
                      marginTop: '4px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <div
                      style={{
                        width: '12px',
                        height: '12px',
                        borderRadius: '50%',
                        background: conf.color,
                        boxShadow: `0 0 8px ${conf.color}`,
                      }}
                    />
                  </div>

                  {/* Experience Card Container */}
                  <div
                    style={{
                      flex: 1,
                      background: 'rgba(255, 255, 255, 0.75)',
                      backdropFilter: 'blur(16px)',
                      WebkitBackdropFilter: 'blur(16px)',
                      border: '1px solid var(--border)',
                      borderRadius: '22px',
                      padding: '1.8rem 2rem',
                      boxShadow: '0 6px 24px rgba(45, 23, 83, 0.04)',
                      transition: 'all 0.3s ease',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.borderColor = 'var(--accent)';
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = '0 12px 32px rgba(232, 149, 109, 0.16)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor = 'var(--border)';
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 6px 24px rgba(45, 23, 83, 0.04)';
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                        flexWrap: 'wrap',
                        gap: '0.75rem',
                        marginBottom: '0.4rem',
                      }}
                    >
                      <h3
                        style={{
                          fontSize: '1.2rem',
                          fontWeight: 600,
                          fontFamily: 'var(--font-heading)',
                          color: 'var(--text)',
                        }}
                      >
                        {item.title}
                      </h3>
                      <span
                        style={{
                          fontSize: '0.72rem',
                          fontWeight: 700,
                          padding: '0.3rem 0.8rem',
                          borderRadius: '100px',
                          background: conf.bg,
                          color: conf.color,
                          border: `1px solid ${conf.border}`,
                          textTransform: 'uppercase',
                          letterSpacing: '0.06em',
                        }}
                      >
                        {conf.label}
                      </span>
                    </div>

                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '1rem',
                        flexWrap: 'wrap',
                        marginBottom: '1rem',
                      }}
                    >
                      <p style={{ fontSize: '0.92rem', color: 'var(--accent-dark)', fontWeight: 600 }}>
                        {item.org}
                      </p>
                      <span style={{ color: 'var(--border)' }}>•</span>
                      <p style={{ fontSize: '0.82rem', color: 'var(--text-muted)' }}>
                        🗓 {item.duration}
                      </p>
                    </div>

                    <p style={{ fontSize: '0.94rem', color: 'var(--text-muted)', lineHeight: 1.7, marginBottom: '1.25rem' }}>
                      {item.description}
                    </p>

                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.45rem' }}>
                      {item.skills.map((s) => (
                        <span
                          key={s}
                          style={{
                            fontSize: '0.76rem',
                            fontWeight: 500,
                            padding: '0.25rem 0.75rem',
                            borderRadius: '100px',
                            background: 'rgba(250, 246, 238, 0.9)',
                            border: '1px solid var(--border)',
                            color: 'var(--text)',
                          }}
                        >
                          {s}
                        </span>
                      ))}
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
